import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Table, Tooltip, Typography } from 'antd';
import { CopyOutlined, DownOutlined, UpOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { debounce } from 'lodash';
import classnames from 'classnames';
import Copy from '@/components/copy';
import IconTip from '@/components/icon-tip';
import { gotoReceiptDetail } from './../../activity-receipt-jump';
import { ActivityReceiptContext } from './../../activity-receipt-context';
import {
  ACTIVITYRECEIPTTYPEENUM,
  RECEIPTEXTFIELDSENUM,
  GATHERTYPEENUM,
  AREALISTENUM,
  computedRatio,
  dateDurationFormat,
} from './../../activity-receipt-config';
import apis from './../../apis';
import './index.less';

export default function ReceiptListTableMemoized({ record, index, expanded, onCloseReceiptList }) {
  const MemoReceiptListTable = useMemo(() => {
    return expanded ? (
      <ReceiptListTable record={record} index={index} expanded={expanded} onCloseReceiptList={onCloseReceiptList} />
    ) : null;
  }, [record, index, expanded, onCloseReceiptList]);
  return <>{MemoReceiptListTable}</>;
}

const initReceiptOpitions = { pageSize: 5, page: 1 };

function ReceiptListTable(props) {
  const { record, expanded, index, onCloseReceiptList } = props;
  const [activeTab] = useContext(ActivityReceiptContext);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptTableList, setReceiptTableList] = useState(null);
  const [receiptExtFieldsObj, setReceiptExtFieldsObj] = useState({});
  const [options, setOptions] = useState(initReceiptOpitions);

  useEffect(() => {
    setReceiptTableList(null);
    setReceiptExtFieldsObj({});
    setReceiptLoading(false);
    setOptions({
      ...initReceiptOpitions,
      activityIdStr: record?.baseInfo?.id || '',
      scene: activeTab,
    });
  }, [activeTab, record?.baseInfo?.id]);

  // 驱动子组件调取api获取数据的唯一途径
  const memoizedGetRecTableList = useCallback(async () => {
    if (!options.scene) return false;
    if (expanded && options && options?.activityIdStr) {
      setReceiptLoading(true);
      let tableData = null;
      try {
        const { data } = await apis.fetchAreaPage({ ...options });
        tableData = data;
        if (data) {
          setReceiptTableList((pre) => {
            if ((!pre && data) || pre?.page !== data?.page) {
              const newItems = [...(pre?.items || []), ...(data?.items || [])];
              return { ...data, items: newItems };
            }
          });
        }
        setReceiptLoading(false);
      } catch (error) {
        setReceiptLoading(false);
      }

      try {
        if (tableData) {
          const { items } = tableData;
          const areaIdsArr = [];
          if (items && Array.isArray(items) && items.length > 0) {
            items.forEach((item) => {
              const { baseInfo } = item;
              areaIdsArr.push(baseInfo?.id);
            });
          }
          if (!(areaIdsArr.length > 0)) {
            return false;
          }
          const areaIds = areaIdsArr.join();
          const {
            data: { data: extData },
          } = await apis.fetchAreaExtFields({
            areaIds,
            scene: options?.scene,
            fields: Object.keys(RECEIPTEXTFIELDSENUM).join(),
          });
          if (extData && Array.isArray(extData) && extData.length > 0) {
            // 这样保证每条数据和他对应的拓展数据都是一一对应的
            const extDataNewObj = extData.reduce((total, item, index) => {
              const receiptExtFieldsFormat = item?.infos?.reduce((ctotal, citem) => {
                return citem && citem?.key ? { ...ctotal, [citem.key]: citem.value } : { ...ctotal };
              }, {});
              return { ...total, [areaIdsArr[index]]: receiptExtFieldsFormat };
            }, {});

            setReceiptExtFieldsObj((pre) => ({ ...pre, ...extDataNewObj }));
          }
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  }, [options, expanded]);

  useEffect(() => {
    memoizedGetRecTableList();
  }, [memoizedGetRecTableList]);

  /* 两种类型的columns */
  const commonColumns = [
    {
      title: (
        <span className="title-component-txt" style={{ paddingLeft: '16px' }}>
          待办收品池
        </span>
      ),
      dataIndex: 'info',
      key: 'info',
      className: 'receipt-columns-custom',
      fixed: 'left',
      width: 288,
      render: (value, { baseInfo }) => {
        return (
          <div className="base-receipt-info-fa" style={{ paddingLeft: '16px' }}>
            <div
              className="base-receipt-type"
              onClick={() => {
                gotoReceiptDetail(activeTab, baseInfo);
              }}
            >
              {GATHERTYPEENUM[baseInfo?.gatherType || '0']?.des || ''}
            </div>
            <div className="base-receipt-name">
              <span
                title={baseInfo?.name || ''}
                onClick={() => {
                  gotoReceiptDetail(activeTab, baseInfo);
                }}
              >
                {baseInfo?.name}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: <span className="title-component-txt">收品池ID</span>,
      dataIndex: 'activityTime',
      key: 'activityTime',
      className: 'receipt-columns-custom',
      render: (value, { baseInfo }) => {
        return (
          <>
            {baseInfo?.id && (
              <Typography.Paragraph
                style={{ marginBottom: 0, lineHeight: '20px' }}
                // copyable={{
                //   text: `收品池ID：${baseInfo?.id || ''}，收品池名称：${baseInfo?.name || ''}`,
                //   icon: <CopyOutlined style={{ color: 'rgba(0, 0, 0, 0.65)' }} />,
                // }}
              >
                <span style={{ color: 'rgba(0, 0, 0, 0.65)' }}>
                  <Copy text={`收品池ID：${baseInfo?.id || ''}，收品池名称：${baseInfo?.name || ''}`} size="small">
                    <span className="receipt-id-fa">{baseInfo?.id || ''}</span>
                  </Copy>
                </span>
              </Typography.Paragraph>
            )}
          </>
        );
      },
    },
    {
      title: <span className="title-component-txt">即将开始场次</span>,
      dataIndex: 'waitTime',
      key: 'waitTime',
      className: 'receipt-columns-custom',
      width: 240,
      render: (value, { baseInfo }) => {
        const baseInfoId = baseInfo?.id || null;
        const receiptExtThisValueById = receiptExtFieldsObj && baseInfoId ? receiptExtFieldsObj[baseInfoId] : null;
        const thisCellShowValue =
          baseInfoId &&
          receiptExtThisValueById &&
          receiptExtThisValueById[RECEIPTEXTFIELDSENUM.SOON_BEGIN_BATCH_TIME.key]
            ? receiptExtThisValueById[RECEIPTEXTFIELDSENUM.SOON_BEGIN_BATCH_TIME.key]
            : null;
        return (
          <div className="receipt-wait-time-fa">
            {baseInfoId && receiptExtThisValueById && thisCellShowValue ? (
              <>
                <span className="receipt-wait-time-show">{moment(thisCellShowValue).format('MM-DD HH:mm')}</span>
                <span className="receipt-wait-time-duration">{dateDurationFormat(thisCellShowValue)}</span>
              </>
            ) : (
              '-'
            )}
          </div>
        );
      },
    },
    {
      title: <span className="title-component-txt">待我审核素材数</span>,
      dataIndex: 'waitDoCheck',
      key: 'waitDoCheck',
      className: 'receipt-columns-custom',
      render: (value, { baseInfo }) => {
        const baseInfoId = baseInfo?.id || null;
        const receiptExtThisValueById = receiptExtFieldsObj && baseInfoId ? receiptExtFieldsObj[baseInfoId] : null;
        const thisCellShowValue =
          baseInfoId && receiptExtThisValueById && receiptExtThisValueById[RECEIPTEXTFIELDSENUM.WAIT_CHECK_COUNT.key]
            ? receiptExtThisValueById[RECEIPTEXTFIELDSENUM.WAIT_CHECK_COUNT.key]
            : null;
        return (
          <>
            {baseInfoId && receiptExtThisValueById && thisCellShowValue && thisCellShowValue > 0 ? (
              <div className="receipt-waitdo-check-count-fa">{thisCellShowValue}</div>
            ) : (
              '-'
            )}
          </>
        );
      },
    },
    {
      title: (
        <>
          <span
            className="title-component-txt"
            // style={{ marginRight: '5px' }}
          >
            完成进度
          </span>
          {/* <Tooltip placement="top" title={`当前收品池中，我已报名资源数+我的下级报名且审核通过的资源数）/我的资源总数`}>
            <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
          </Tooltip> */}
          <IconTip
            type="question"
            title={`当前收品池中，我已报名资源数+我的下级报名且审核通过的资源数）/我的资源总数`}
          />
        </>
      ),
      dataIndex: 'completeRatio',
      key: 'completeRatio',
      className: 'receipt-columns-custom',
      render: (value, { baseInfo }) => {
        const baseInfoId = baseInfo?.id || null;
        const receiptExtThisValueById = receiptExtFieldsObj && baseInfoId ? receiptExtFieldsObj[baseInfoId] : null;
        const thisCellShowValue =
          baseInfoId && receiptExtThisValueById && receiptExtThisValueById[RECEIPTEXTFIELDSENUM.COMPLETE_RATIO.key]
            ? receiptExtThisValueById[RECEIPTEXTFIELDSENUM.COMPLETE_RATIO.key]
            : null;
        return (
          <div
            className={classnames(
              'receipt-complete-ratio-fa',
              computedRatio(thisCellShowValue)[0]
                ? computedRatio(thisCellShowValue)[1] === 'OVERDATE'
                  ? 'has-receipt-overDate'
                  : 'has-receipt-complete'
                : thisCellShowValue
                ? 'no-receipt-complete'
                : ''
            )}
          >
            {thisCellShowValue || '-'}
          </div>
        );
      },
    },
    {
      title: <span className="title-component-txt">操作</span>,
      key: 'action',
      className: 'receipt-columns-custom',
      fixed: 'right',
      width: 164,
      render: (text, { baseInfo, buttons }) => {
        let includeCollectBtns = [];
        if (buttons && Array.isArray(buttons) && buttons.length > 0) {
          includeCollectBtns = buttons.filter((item) => item.code == AREALISTENUM?.YX_AREA_LIST_VIEW?.key);
        }
        return (
          <div className="buttons-fa">
            <div className="buttons-common-fa">
              {/* 只有查看详情 */}
              {includeCollectBtns.length > 0
                ? includeCollectBtns.map((item) => (
                    <span
                      key={item.code}
                      onClick={() => {
                        gotoReceiptDetail(activeTab, baseInfo);
                      }}
                      className="receipt-btn-common-item"
                    >
                      {item.name}
                    </span>
                  ))
                : null}
            </div>
          </div>
        );
      },
    },
  ];
  /* 报名特有的列 */
  const manageExtColumns = [
    {
      title: <span className="title-component-txt">最新获得资源时间</span>,
      dataIndex: 'lastAssignTime',
      key: 'lastAssignTime',
      className: 'receipt-columns-custom',
      render: (value, { baseInfo }) => {
        return (
          <span className="receipt-last-assign-time-fa">
            {baseInfo?.lastAssignTime ? moment(baseInfo.lastAssignTime).format('MM-DD HH:mm') : '-'}
          </span>
        );
      },
    },
    {
      title: <span className="title-component-txt">待我报名场次数</span>,
      dataIndex: 'waitDoArea',
      key: 'waitDoArea',
      className: 'receipt-columns-custom',
      render: (value, { baseInfo }) => {
        const baseInfoId = baseInfo?.id || null;
        const receiptExtThisValueById = receiptExtFieldsObj && baseInfoId ? receiptExtFieldsObj[baseInfoId] : null;
        const thisCellShowValue =
          baseInfoId && receiptExtThisValueById && receiptExtThisValueById[RECEIPTEXTFIELDSENUM.USEABLE_BATCH_COUNT.key]
            ? receiptExtThisValueById[RECEIPTEXTFIELDSENUM.USEABLE_BATCH_COUNT.key]
            : null;
        return (
          <>
            {baseInfoId && receiptExtThisValueById && thisCellShowValue && thisCellShowValue > 0 ? (
              <div className="receipt-waitdo-area-count-fa">{thisCellShowValue}</div>
            ) : (
              '-'
            )}
          </>
        );
      },
    },
  ];

  let showClumns = [...commonColumns];
  if (activeTab === ACTIVITYRECEIPTTYPEENUM.APPLY.key) {
    showClumns.splice(2, 0, manageExtColumns[0]);
    showClumns.splice(4, 0, manageExtColumns[1]);
  }

  function getReceiptListMore() {
    setOptions((pre) => ({ ...pre, page: pre.page + 1 }));
  }

  return (
    <div className="receipt-list-table-fa">
      <Table
        scroll={{ x: 1300 }}
        pagination={false}
        loading={receiptLoading}
        columns={showClumns}
        rowKey={({ baseInfo }) => baseInfo?.id || 'rowKey'}
        className="receipt-list-table"
        rowClassName={() => 'receipt-list-row-custom'}
        dataSource={receiptTableList?.items || []}
        footer={() => {
          let computedRegNum =
            receiptTableList?.totalCount && receiptTableList?.totalCount > 0
              ? receiptTableList?.totalCount - (receiptTableList?.pageSize || 5) * receiptTableList?.page
              : 0;

          return (
            <div className="receipt-list-more-wrapper">
              {computedRegNum > 0 ? (
                <div className="receipt-list-more-fa" onClick={debounce(getReceiptListMore, 200)}>
                  <span className="receipt-list-more-txt">展开更多收品池（{computedRegNum}）</span>
                  <span className="receipt-list-more-icon">
                    <DownOutlined />
                  </span>
                </div>
              ) : (
                <div className="receipt-list-no-more-fa" onClick={debounce(onCloseReceiptList, 200)}>
                  <span className="receipt-list-no-more-txt">收起全部收品池</span>
                  <span className="receipt-list-no-more-icon">
                    <UpOutlined />
                  </span>
                </div>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
